// personnummer.js - visning av personnummer i 10-siffrigt Pasis-format
//
// Backend lagrar personnummer kanoniskt som 12 siffror (ÅÅÅÅMMDDXXXX). Vård-
// personalen läser dem i 10-siffrigt Pasis-format (ÅÅMMDDXXXX, utan streck).
// Denna helper översätter enbart för VISNING — inmatning skickas rått till
// backend som normaliserar (se backend/pnr_utils.py). Ålder räknas fortsatt ur
// det lagrade 12-siffriga värdet, inte ur detta.
//
// Reservnummer/utländskt/annat id (patient.id_type, Spår P3) är valfri text i
// samma fält: visas och kopieras som det står, och ger aldrig någon ålder.

export const ID_TYPES = [
  { value: 'personnummer', label: 'Personnummer' },
  { value: 'samordningsnummer', label: 'Samordningsnummer' },
  { value: 'reservnummer', label: 'Reservnummer' },
  { value: 'utlandskt', label: 'Utländskt id' },
  { value: 'annat', label: 'Annat id' },
];

const PNR_ID_TYPES = ['personnummer', 'samordningsnummer'];

// Saknad typ = personnummer (äldre svar/objekt utan id_type)
export const isPnrIdType = (idType) => !idType || PNR_ID_TYPES.includes(idType);

export const idTypeLabel = (idType) =>
  (ID_TYPES.find(t => t.value === idType) || ID_TYPES[0]).label;

export const formatPnrPasis = (personnummer, idType) => {
  if (!personnummer) return '';
  if (!isPnrIdType(idType)) return String(personnummer);
  const digits = String(personnummer).replace(/\D/g, '');
  // Kanoniskt 12-siffrigt → släpp sekelsiffrorna. Redan 10-siffrigt returneras
  // rensat; okänd längd visas som det står (får aldrig dölja data).
  if (digits.length === 12) return digits.slice(2);
  if (digits.length === 10) return digits;
  return String(personnummer);
};

// Födelsedatum ur 12-siffrigt personnummer/samordningsnummer, annars null.
const birthDateFromPnr = (personnummer, idType) => {
  if (!personnummer || !isPnrIdType(idType)) return null;
  const digits = String(personnummer).replace(/\D/g, '');
  if (digits.length !== 12) return null;
  const year = parseInt(digits.slice(0, 4), 10);
  const month = parseInt(digits.slice(4, 6), 10);
  let day = parseInt(digits.slice(6, 8), 10);
  // Samordningsnummer: dagen är +60 (t.ex. 61–91) — dra bort för att få födelsedag.
  if (day > 60) day -= 60;
  const born = new Date(year, month - 1, day);
  if (born.getFullYear() !== year || born.getMonth() !== month - 1 || born.getDate() !== day) {
    return null;
  }
  return born;
};

// Ålder som text: "16 mån" under 2 år, annars "N år". null om åldern inte kan
// avgöras (reservnummer/utländskt id, fel längd, ogiltigt datum, framtida datum).
export const formatAgeFromPnr = (personnummer, idType, today = new Date()) => {
  const born = birthDateFromPnr(personnummer, idType);
  if (!born) return null;
  let months = (today.getFullYear() - born.getFullYear()) * 12
    + (today.getMonth() - born.getMonth());
  if (today.getDate() < born.getDate()) months -= 1;
  if (months < 0 || months > 130 * 12) return null;
  if (months < 24) return `${months} mån`;
  return `${Math.floor(months / 12)} år`;
};
